import { ReactNode } from 'react';
import { X as CloseIcon } from 'lucide-react';

import { classNameList } from 'utils/property';
import { TranslationsKeys } from 'types/translation';
import { useTranslate } from 'hooks/useTranslate';
import { Button } from 'components/ui/Button';
import styles from './Modal.module.scss';

interface IProps {
  title: string;
  primaryButtonLabel?: ReactNode;
  primaryButtonVariant?: 'primary' | 'secondary' | 'tertiary' | 'danger';
  className?: string;
  wide?: boolean;
  heading?: ReactNode;
  description?: ReactNode;
  additionalContent?: ReactNode;
  secondaryButtonLabel?: ReactNode;
  secondaryButtonVariant?: 'primary' | 'secondary' | 'tertiary' | 'danger';
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  onCancel?: () => void;
}

export const Modal = ({
  title,
  primaryButtonLabel,
  primaryButtonVariant,
  className,
  wide,
  heading,
  description,
  additionalContent,
  secondaryButtonLabel,
  secondaryButtonVariant,
  onPrimaryClick,
  onSecondaryClick,
  onCancel
}: IProps): JSX.Element => {
  const { t } = useTranslate();

  return (
    <div className={classNameList(styles.modal, className)}>
      <div className={classNameList(styles.modalContainer, wide && styles.wide)}>
        <div className={styles.modalTitle}>
          <div>{title}</div>
          <CloseIcon
            className={styles.modalCloseButton}
            onClick={onSecondaryClick}
            aria-label={t(TranslationsKeys.common_close)}
          />
        </div>
        <div className={classNameList(styles.modalContent, wide && styles.wide)}>
          {heading && (
            <div
              className={classNameList(
                styles.modalContentHeading,
                !!description && styles.modalContentHeadingWithDescription
              )}>
              {heading}
            </div>
          )}
          {description && <div className={styles.modalContentDescription}>{description}</div>}
          {additionalContent}
        </div>
        <div className={styles.modalActions}>
          {onPrimaryClick && <Button variant={primaryButtonVariant ?? 'primary'} size="large" onClick={onPrimaryClick}>
            {primaryButtonLabel}
          </Button>}
          {secondaryButtonLabel && onSecondaryClick && (
            <Button variant={secondaryButtonVariant ?? 'secondary'} size="large" onClick={onSecondaryClick}>
              {secondaryButtonLabel}
            </Button>
          )}
          {onCancel && (
            <Button variant="tertiary" size="large" onClick={onCancel}>
              {t(TranslationsKeys.common_cancel)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
